/**
 * Admin "Fetch data" card for one participant: fetch a date range into BigQuery and follow this
 * participant's recent fetches live (manual and daily, with progress). Connection details, the
 * connection check and Disconnect are in the Profile card on the participant page.
 */
import {
  collection, limit, onSnapshot, orderBy, query, where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useAlert } from '../../context/AlertContext';
import { db, functions } from '../../firebase';
import cancelFetch from './cancelFetch';

const todayMinus = (days) => new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);

const RECENT_JOBS = 5;

const STATUS_LABEL = {
  queued: 'queued',
  running: 'running',
  done: 'done',
  done_with_errors: 'done with errors',
  cancelled: 'cancelled',
};

export default function HealthPanel({ participantId, projectId, health }) {
  const { addAlert } = useAlert();
  const [busy, setBusy] = useState(false);
  const [startDate, setStartDate] = useState(todayMinus(7));
  const [endDate, setEndDate] = useState(todayMinus(1));
  const [jobs, setJobs] = useState([]);

  // This participant's recent fetches, live: the project's latest jobs (same query and index as the
  // project's Google Health tab), keeping those that include this participant.
  useEffect(() => {
    if (!projectId) return undefined;
    return onSnapshot(
      query(collection(db, 'healthFetchJobs'), where('projectId', '==', projectId),
        orderBy('createdAt', 'desc'), limit(30)),
      (snap) => setJobs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .filter((j) => (j.participantIds || []).includes(participantId))
        .slice(0, RECENT_JOBS)),
      (err) => console.error('healthFetchJobs query failed', err),
    );
  }, [projectId, participantId]);

  const startFetch = async () => {
    setBusy(true);
    try {
      const res = await httpsCallable(functions, 'startHealthFetch')({
        participantIds: [participantId], startDate, endDate,
      });
      addAlert('success', `Fetch queued (${res.data.tasks} task${res.data.tasks === 1 ? '' : 's'})`);
    } catch (err) {
      addAlert('error', `Could not start fetch: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const connected = health?.connected === true;

  return (
    <div className="ja-card" style={{ marginBottom: 20 }}>
      <div className="ja-card-header">
        <h3 className="ja-card-title">Fetch data</h3>
      </div>

      {!connected && (
        <p style={{ fontSize: 13, color: 'var(--jp-gray-500)', margin: 0 }}>
          This participant is not connected to Google Health, so no new data can be fetched.
        </p>
      )}

      {connected && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13 }}>From<br />
              <input type="date" className="ja-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
            <label style={{ fontSize: 13 }}>To (inclusive)<br />
              <input type="date" className="ja-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </label>
            <button className="ja-btn ja-btn-primary" disabled={busy || !startDate || !endDate} onClick={startFetch}>
              Fetch into BigQuery
            </button>
          </div>

          {jobs.length > 0 && (
            <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Recent fetches</div>
              {jobs.map((j) => {
                const total = j.tasksTotal || 0;
                const finished = (j.tasksDone || 0) + (j.tasksFailed || 0) + (j.tasksCancelled || 0);
                const pct = total ? Math.round((finished / total) * 100) : 100;
                const active = j.status === 'queued' || j.status === 'running';
                return (
                  <div key={j.id} style={{ fontSize: 13 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between' }}>
                      <span>
                        {j.createdAt?.toDate ? j.createdAt.toDate().toLocaleString() : '…'} · {j.type || 'manual'}
                        {' '}· {j.startDate}{j.endDate !== j.startDate ? ` to ${j.endDate}` : ''}
                      </span>
                      <span>
                        <strong>{STATUS_LABEL[j.status] || j.status}</strong>
                        {' '}· {finished}/{total} tasks{j.tasksFailed ? ` (${j.tasksFailed} failed)` : ''}
                        {' '}· {(j.rowsWritten || 0).toLocaleString()} rows
                        {active && (
                          <>
                            {' '}
                            <button type="button" className="ja-btn ja-btn-ghost" style={{ padding: '2px 8px' }}
                              onClick={() => cancelFetch(j.id, addAlert)}>
                              Cancel
                            </button>
                          </>
                        )}
                      </span>
                    </div>
                    {active && (
                      <div role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
                        aria-label="Fetch progress"
                        style={{ marginTop: 6, height: 8, background: 'var(--jp-gray-100)', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--jp-blue-mid)', transition: 'width 300ms' }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

HealthPanel.propTypes = {
  participantId: PropTypes.string.isRequired,
  projectId: PropTypes.string,
  health: PropTypes.shape({
    connected: PropTypes.bool,
    status: PropTypes.string,
    healthUserId: PropTypes.string,
    legacyUserId: PropTypes.string,
  }),
};
